import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { reportClientError } from "@/lib/report-error";
import { ErrorState } from "./ErrorState";

export interface AppErrorBoundaryProps {
  /** Human label for the failed view (e.g. the tab kind) — folded into
   * ErrorState's title and attached to the error report. */
  label?: string;
  /** When this value changes, a previously-caught error is cleared — so
   * moving to a different tab/document recovers a boundary that had failed. */
  resetKey?: unknown;
  /** An optional SECONDARY recovery action rendered alongside ErrorState's
   * standard "Try again" — e.g. a per-panel boundary offering "Close panel"
   * so one bad view degrades to one dismissable tab instead of a retry loop.
   * Omit and the fallback is exactly ErrorState's retry, unchanged. */
  action?: { label: string; onClick: () => void };
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Catches render/lifecycle throws in its subtree so one bad view — a
 * malformed PDF, an unexpected node shape, a canvas paint error — shows a
 * recoverable panel instead of white-screening the whole studio (tabs share
 * one tree). Renders the shared {@link ErrorState}, so a caught render crash
 * looks and behaves exactly like every other failure in the app: plain copy,
 * a "Try again" that resets just this boundary, never a raw stack on screen.
 * The full technical detail still reaches `reportClientError` for the actual
 * error report — it's just never painted. */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError(error, {
      boundary: this.props.label ?? "app",
      componentStack: info.componentStack,
    });
  }

  componentDidUpdate(prev: AppErrorBoundaryProps): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private readonly reset = () => this.setState({ error: null });

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const { action } = this.props;
    return (
      <div className="grid h-full w-full place-items-center p-6">
        <div className="panel max-w-sm">
          <ErrorState error={this.state.error} onRetry={this.reset} label={this.props.label} />
          {action && (
            <div className="mt-3 flex justify-center">
              <Button type="button" variant="outline" size="sm" onClick={action.onClick}>
                {action.label}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
}
